import { yupResolver } from "@hookform/resolvers/yup";
import React, { FC, useEffect, useMemo } from "react";
import { FormProvider, Controller, useForm } from "react-hook-form";
import { number, object, string } from "yup";
import slugify from "slugify";
import { FormBaseType, FormBasicsDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { getAvailableFormBaseTypes } from "./CreateUpdateFormDialog.helpers";

import {
  Stack,
  Button,
  TextField,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  CircularProgress,
  Typography,
  Box,
} from "@components/base";
import { Dialog } from "@components/common";
import {
  useCreateFormConfig,
  useGetCurrentCompanyInfo,
  useGetFormsConfigs,
  useUpdateFormConfig,
} from "@api";
import { useUserStore } from "@store/user";

interface CreateUpdateFormDialog {
  open: boolean;
  onClose: () => void;
  formBasics?: FormBasicsDto;
}

const CreateUpdateFormDialog: FC<CreateUpdateFormDialog> = ({
  open,
  onClose,
  formBasics,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const {
    createFormConfigAsync,
    createFormConfigEnabled,
    isPendingCreateFormConfig,
  } = useCreateFormConfig(user);
  const {
    updateFormConfigAsync,
    updateFormConfigEnabled,
    isPendingUpdateFormConfig,
  } = useUpdateFormConfig(user);
  const isLoading = isPendingCreateFormConfig || isPendingUpdateFormConfig;
  const { formsConfigs } = useGetFormsConfigs(user);
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const formBaseTypeOptions = useMemo(() => {
    if (!currentCompanyInfo) {
      return [];
    }

    return getAvailableFormBaseTypes(currentCompanyInfo);
  }, [currentCompanyInfo]);

  const schema = object({
    formName: string()
      .min(1, t("validation.requiredLengthAtleastOne"))
      .max(50, "validation.requiredLengthNoLongerThanFifty")
      .required(t("validation.required")),
    action: string<"create" | "copy">().required(),
    formBaseType: number().optional(),
    templateId: string().optional(),
  }).required();
  const methods = useForm<{
    formName: string;
    action: "create" | "copy";
    formBaseType?: number;
    templateId?: string;
  }>({
    defaultValues: {
      formName: "",
      action: "create",
      formBaseType: formBasics?.formBaseType ?? FormBaseType.Co2,
      templateId: undefined,
    },
    mode: "onChange",
    resolver: yupResolver(schema),
  });
  const formName = methods.watch("formName");
  const action = methods.watch("action");
  const templateId = methods.watch("templateId");
  const formBaseType = methods.watch("formBaseType");
  const editMode = Boolean(formBasics);

  const formTemplates = useMemo(
    () => formsConfigs?.items ?? [],
    [formsConfigs?.items],
  );

  const handleSave = async (data: {
    formName: string;
    formBaseType?: number;
  }): Promise<void> => {
    if (formBasics) {
      await updateFormConfigAsync({
        formId: formBasics.id,
        formConfig: {
          name: data.formName,
          code: slugify(data.formName, { lower: true }),
          formBaseType: data.formBaseType,
        },
      });
    } else {
      await createFormConfigAsync({
        name: data.formName,
        code: slugify(data.formName, { lower: true }),
        ...(action === "create" && {
          formBaseType: data.formBaseType,
        }),
        templateId: action === "copy" ? (templateId ?? "") : "",
      });
    }
    methods.reset();
    onClose();
  };

  useEffect(() => {
    if (formBasics) {
      methods.setValue("formName", formBasics.name || "");
    } else {
      methods.reset({
        formName: "",
      });
    }
  }, [formBasics, open]);

  return (
    ((formBasics && updateFormConfigEnabled) ||
      (!formBasics && createFormConfigEnabled)) && (
      <Dialog
        title={
          isLoading
            ? ""
            : formBasics
              ? t("formBuilder.editForm")
              : t("formBuilder.createNewForm")
        }
        isOpen={open}
        onClose={isLoading ? undefined : onClose}
        dialogProps={{ maxWidth: "xs", fullWidth: true }}
      >
        {isLoading ? (
          <Stack alignItems="center" py={12} spacing={1}>
            <Box textAlign="center" pb={2}>
              <CircularProgress />
            </Box>
            <Typography variant="subtitle1">
              {t("formBuilder.creatingForm")}
            </Typography>
            <Typography>{t("formBuilder.creatingFormDescription")}</Typography>
          </Stack>
        ) : (
          <FormProvider {...methods}>
            <form>
              <Stack spacing={3}>
                <Controller
                  control={methods.control}
                  name="formName"
                  render={({ field, fieldState: { error } }): React.ReactElement => (
                    <TextField
                      {...field}
                      error={Boolean(error)}
                      helperText={error?.message}
                      color="secondary"
                      variant="filled"
                      placeholder="Form name"
                      fullWidth
                      autoFocus
                    />
                  )}
                />
                {!editMode && (
                  <Controller
                    control={methods.control}
                    name="action"
                    render={({ field }): React.ReactElement => (
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="create"
                          control={<Radio />}
                          label={t("formBuilder.createEmptyForm")}
                        />
                        <FormControlLabel
                          value="copy"
                          control={<Radio />}
                          label={t("formBuilder.copyForm")}
                        />
                      </RadioGroup>
                    )}
                  />
                )}
                {action === "create" && (
                  <Controller
                    control={methods.control}
                    name="formBaseType"
                    render={({
                      field,
                      fieldState: { error },
                    }): React.ReactElement => {
                      const isError = Boolean(error);
                      const options = formBaseTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ));

                      return (
                        <FormControl error={isError}>
                          <InputLabel>{t("formBuilder.formBaseType")}</InputLabel>
                          <Select {...field}>{options}</Select>
                        </FormControl>
                      );
                    }}
                  />
                )}
                {action === "copy" && (
                  <Controller
                    control={methods.control}
                    name="templateId"
                    render={({
                      field,
                      fieldState: { error },
                    }): React.ReactElement => {
                      const isError = Boolean(error);
                      const options = formTemplates.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ));

                      return (
                        <FormControl error={isError}>
                          <InputLabel>
                            {t("formBuilder.selectFormTemplate")}
                          </InputLabel>
                          <Select {...field}>{options}</Select>
                        </FormControl>
                      );
                    }}
                  />
                )}
                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Button
                    type="reset"
                    variant="outlined"
                    disabled={isPendingCreateFormConfig || isPendingUpdateFormConfig}
                    onClick={onClose}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    disabled={
                      !formName ||
                      !action ||
                      (action === "copy" && !templateId) ||
                      (action === "create" && formBaseType === undefined)
                    }
                    onClick={methods.handleSubmit(handleSave)}
                    isLoading={
                      isPendingCreateFormConfig || isPendingUpdateFormConfig
                    }
                  >
                    {methods.formState.isValid
                      ? formBasics
                        ? t("formBuilder.editFormButton")
                        : t("formBuilder.createNewFormButton")
                      : t("formBuilder.fillInFormName")}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        )}
      </Dialog>
    )
  );
};

export default CreateUpdateFormDialog;
